/**
 * Pre-warms the on-demand chunks a tab would otherwise fetch on first use.
 *
 * Vercel deletes the previous build's hashed files on every deploy, so a tab
 * opened before a deploy can't fetch a split-out chunk afterwards and the
 * feature that needs it breaks mid-meeting. Loading those chunks a few
 * seconds after the app mounts, in idle time, puts them in the tab's module
 * map; a later import resolves from there and never touches the network, so
 * a deploy can't take them away.
 *
 * - The 1040 importer's PDF reader (pdfjs + its worker).
 * - The PDF export's chart capture and SVG-to-PDF writer. Andrew, 2026-11-02:
 *   Export PDF did nothing in a tab opened before that morning's deploy,
 *   because the capture module 404'd on first use.
 */

export const EXPORT_CHUNKS: ReadonlyArray<() => Promise<unknown>> = [
  () => import('@/lib/journeyCapture'),
  () => import('svg2pdf.js'),
];

async function warmPdfReader(): Promise<void> {
  const { warmPdfExtractor } = await import('@/lib/extractPdfText');
  await warmPdfExtractor();
}

/** Every warm-up runs on its own, so one that fails never stops the others. */
export async function warmAllChunks(): Promise<void> {
  await Promise.allSettled([warmPdfReader(), ...EXPORT_CHUNKS.map(load => load())]);
}

/**
 * Schedule the warm-up after `delayMs` (yielding to idle time when the
 * browser supports it). Failures are swallowed — a warm-up that doesn't land
 * just leaves the tab where it was. Returns a cancel function.
 */
export function scheduleChunkWarmup(load: () => Promise<void> = warmAllChunks, delayMs = 4000): () => void {
  let cancelled = false;
  let idleHandle: number | null = null;
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void) => number;
    cancelIdleCallback?: (h: number) => void;
  };

  const run = () => {
    if (cancelled) return;
    load().catch(() => {});
  };

  const timer = setTimeout(() => {
    if (cancelled) return;
    if (typeof w.requestIdleCallback === 'function') idleHandle = w.requestIdleCallback(run);
    else run();
  }, delayMs);

  return () => {
    cancelled = true;
    clearTimeout(timer);
    if (idleHandle !== null && typeof w.cancelIdleCallback === 'function') w.cancelIdleCallback(idleHandle);
  };
}
